import { Component, Input, OnChanges, OnDestroy, SimpleChanges, TemplateRef } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Vehicle, VehicleCurrentInfo } from 'src/app/_shared/models/vehicle';
import { VehicleInfoService } from 'src/app/_services/vehicle-info.service';
import { VehicleGeneralFigureService } from 'src/app/_services/vehicle-general-figure.service';
import { CertificateService } from 'src/app/_services/certificate.service';
import { ModalService, ToasterService } from '@inst-iot/bosch-angular-ui-components';
import { NotificationToastComponent } from 'src/app/_shared/components/utils/notification-toast/notification-toast.component';
import { KmToMilesPipe } from 'src/app/_shared/pipes/km-to-miles.pipe';
import { RemoveDecimalPointsPipe } from 'src/app/_shared/pipes/remove-decimal-points.pipe';
import { KWhKmToMilesPipe } from 'src/app/_shared/pipes/k-wh-km-to-miles.pipe';


@Component({
  selector: 'app-general-vehicles',
  templateUrl: './general-vehicles.component.html',
  styleUrls: ['./general-vehicles.component.scss'],
  providers: [
    {
      provide: ToasterService,
      useFactory: () => new ToasterService(NotificationToastComponent)
    }
  ]
})
export class GeneralVehiclesComponent implements OnChanges, OnDestroy {
  private readonly unsubscribe$ = new Subject<void>();
  @Input() compareToFleet: boolean = false;
  @Input() selectedVehicles: any[] = [];
  @Input() chipSelectedVehicle: any;
  @Input() fleetId: string[] = [];
  @Input() dataLoadingError: boolean = false;
  @Input() companyData: any;
  @Input() isMetric: boolean | undefined;
  private readonly kmToMilesPipe = new KmToMilesPipe();
  private readonly removeDecimalPointsPipe = new RemoveDecimalPointsPipe();
  private readonly kWhKmToMilesPipe = new KWhKmToMilesPipe();
  vehiclesInfo: { [key: string]: VehicleCurrentInfo; } = {};
  certificatesStatus: { [key: string]: any; } = {};
  currentVehicleInsights: VehicleCurrentInfo = {
    agingClass: '',
    avgEvEfficiency: 0,
    currEnergy: 0,
    currRange: 0,
    batteryStateOfHealth: 0,
    evStatusCode: 0,
    rating: '',
    mileage: 0,
    predRange: 0,
    predSohc: 0,
    timestamp: '',
    timeWindowsStart: '',
    batteryIdentifier: '',
    usableCurrEnergy: 0,
    usableInitialEnergy: 0,
    resultStatus: '',
    resultReasons: [''],
    WLTPTELRemaining: 0,
    WLTPTEHRemaining: 0,
    EPARemaining: 0,
    WLTPTELPrediction: 0,
    WLTPTEHPrediction: 0,
    EPAPrediction: 0,
    WLTPTELBOL: 0,
    WLTPTEHBOL: 0,
    EPABOL: 0,
    predictionDays: 0
  }
  currentVehicleData!: Vehicle;
  errorMessage: boolean = false;
  dataLoading: boolean = false;
  stressFactorInfo: any;
  buttonLabels = {
    confirmLabel: 'Download',
    cancelLabel: 'Cancel',
    optionalLabel: 'Optional'
  };
  headline: string | undefined;
  buttonClickedResult: string | undefined;
  dynamicTabItems: any[] = [];
  disableCertificate: boolean = false;
  sohTrajectories: any;
  errorMessageSohTrajectories: boolean = false;
  dataLoadingSohTrajectories: boolean = false;

  constructor(
    private readonly vehicleInfoService: VehicleInfoService,
    private readonly vehicleGeneralFigureService: VehicleGeneralFigureService,
    private readonly certificateService: CertificateService,
    private readonly modalService: ModalService,
    private readonly toaster: ToasterService
  ) { }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.dataLoading = true;
    this.errorMessage = false;
    this.currentVehicleData = this.chipSelectedVehicle || this.selectedVehicles[0]
    this.dynamicTabItems = [];
    Object.values(this.selectedVehicles).forEach(vehicle => {
      this.dynamicTabItems.push(vehicle);
    });
    const deviceSerialNumber: string[] = this.selectedVehicles.map(vehicle => vehicle.batteryIdentifier);
    this.fetchCurrentVehiclesInsights(deviceSerialNumber, this.dynamicTabItems);
    this.fetchCertificateStatus(deviceSerialNumber);
    this.fetchSohTrajectories(deviceSerialNumber);
    if (deviceSerialNumber.length > 0) {
      this.vehicleGeneralFigureService.getStressFactors(deviceSerialNumber).pipe(takeUntil(this.unsubscribe$)).subscribe(
        {
          next: (data: any) => {
            this.dynamicTabItems.forEach((vehicle: any) => {
              this.stressFactorInfo = data[vehicle.batteryIdentifier]?.stressFactors;
              if (this.stressFactorInfo) {
                vehicle.stressFactorInfo = this.stressFactorInfo;
              }
            });
          },
          error: (error) => {
            console.error('Error fetching Stress Factors data:', error);
            this.dataLoading = false;
            this.errorMessage = true;
          },
          complete: () => {
            this.dataLoading = false;
          }
        }
      );
    } else if (this.dataLoadingError) {
      this.dataLoading = false;
      this.errorMessage = true;
    }
  }

  fetchCurrentVehiclesInsights(deviceSerialNumber: string[], dynamicTabItems: any) {
    if (deviceSerialNumber.length > 0) {
      this.vehicleInfoService.getCurrentVehicleInfo(deviceSerialNumber).pipe(takeUntil(this.unsubscribe$)).subscribe(
        {
          next: (data) => {
            this.vehiclesInfo = data;
            dynamicTabItems.forEach((vehicle: any) => {
              const insights = this.vehiclesInfo[vehicle.batteryIdentifier];
              if (insights) {
                vehicle.currentVehicleInsights = insights;
              }
            });
          },
          error: (error) => {
            console.error('Error fetching Current Vehicle Info data:', error);
            this.errorMessage = true;
          },
          complete: () => {
            this.dataLoading = false;
          }
        }
      );
    } 
  }

  fetchCertificateStatus(deviceSerialNumber: string[]) {
    if (deviceSerialNumber.length > 0) {
      this.certificateService.getCertificateStatus(deviceSerialNumber).pipe(takeUntil(this.unsubscribe$)).subscribe(
        {
          next: (data) => {
            this.certificatesStatus = data;
            this.dynamicTabItems.forEach((vehicle: any) => {
              const certificate = this.certificatesStatus[vehicle.batteryIdentifier];
              if (certificate) {
                vehicle.certificateStatus = certificate;
              }
            });
          },
          error: (error) => {
            this.disableCertificate = true;
            console.error('Error fetching Certificate Status data:', error);
          },
          complete: () => {
            
          }
        }
      );
    }
  }

  fetchSohTrajectories(deviceSerialNumber: string[]) {
    if (deviceSerialNumber.length > 0) {
      this.vehicleGeneralFigureService.getSohTrajectories(deviceSerialNumber).pipe(takeUntil(this.unsubscribe$)).subscribe(
        {
          next: (data) => {
            this.sohTrajectories = data;
          },
          error: (error) => {
            console.error('Error fetching SOH Trajectories data:', error);
            this.sohTrajectories = null;
            this.dataLoadingSohTrajectories = false;
            this.errorMessageSohTrajectories = true;
          },
          complete: () => {
            this.dataLoadingSohTrajectories = false;
          }
        }
      );
    }
  }

  requestCertificate(deviceSerialNumber: string) {
    const body = {"batteryIdentifier": deviceSerialNumber }
    this.certificateService.requestCertificate(body).pipe(takeUntil(this.unsubscribe$)).subscribe(
      {
        next: (response) => {
          
        },
        error: (error) => {
          console.error('Error requesting certificate:', error);
        },
        complete: () => {
          const label = `The requested certificate is being processed and will take about a minute to be issued. You can wait for the certificate to be issued on this page or explore the other pages.`;
          const icon = `rb-ic-alert-success`;
          this.openToast(label, icon, 'success');
          setTimeout(()=>this.fetchCertificateStatus([deviceSerialNumber]), 500);
          setTimeout(()=>this.fetchCertificateStatus([deviceSerialNumber]), 60000);
        }
      }
    )
  }

  openToast(label: string, icon: string, type: 'error' | 'success' | 'neutral') {
    this.toaster.createToast(
      label,
      icon,
      { 
        timeout: 10000,
        customProperties: { margin: '0px', type: type }
      }
    );
  }

  openDialog(tpl: TemplateRef<any>, deviceSerialNumber: string) {
    this.modalService.open(tpl, { maxWidth: '650px', }).then(close => {
      if (close) {
        this.certificateService.getCertificate(deviceSerialNumber).pipe(takeUntil(this.unsubscribe$)).subscribe(
        {
          next: (data) => {
            const url = this.cleanUrl(data.url);
            window.open(url);
          },
          error: (error) => {
            console.error('Error downloading certificate:', error);
          },
          complete: () => {
            
          }
        }
      );
      }
    });
  }

  cleanUrl(rawUrl: string): string {
    if (!rawUrl) return '';
    
    let url = rawUrl.split('\\"').join('').trim();
    let start = 0;
    while (start < url.length && url[start] === '"') {
      start++;
    }
    let end = url.length;
    while (end > start && url[end - 1] === '"') {
      end--;
    }
    url = url.slice(start, end).trim();
    const httpIndex = url.indexOf('http');
    url = httpIndex !== -1 ? url.substring(httpIndex) : url;
    
    end = url.length;
    while (end > 0) {
      const character = url[end - 1];
      if (character !== '"' && character !== "'" && character !== '\\' && character.trim() !== '') break;
      end--;
    }
    return url.slice(0, end);
  }

  getFormattedMileage(mileage: number | null | undefined): string {
    if (mileage === null || mileage === undefined) return 'N/A';
    if (this.isMetric !== null && this.isMetric !== undefined) {
      return this.isMetric
        ? `${this.removeDecimalPointsPipe.transform(mileage, 0)} km`
        : `${this.removeDecimalPointsPipe.transform(this.kmToMilesPipe.transform(mileage), 0)} mi`;
    } else {
      return this.companyData?.companyAddress?.country === 'United States (US)'
        ? `${this.removeDecimalPointsPipe.transform(this.kmToMilesPipe.transform(mileage), 0)} mi`
        : `${this.removeDecimalPointsPipe.transform(mileage, 0)} km`;
    }
  }

  getFormattedRange(range: number | null | undefined): string {
    if (range === null || range === undefined) return 'N/A';
    if (this.isMetric !== null && this.isMetric !== undefined) {
      return this.isMetric
        ? `${this.removeDecimalPointsPipe.transform(range, 0)} km`
        : `${this.removeDecimalPointsPipe.transform(this.kmToMilesPipe.transform(range), 0)} mi`;
    } else {
      return this.companyData?.companyAddress?.country === 'United States (US)'
        ? `${this.removeDecimalPointsPipe.transform(this.kmToMilesPipe.transform(range), 0)} mi`
        : `${this.removeDecimalPointsPipe.transform(range, 0)} km`;
    }
  }

  getFormattedAvgEfficiency(eff: number | null | undefined): string {
    if (eff === null || eff === undefined) return 'N/A';
    if (this.isMetric !== null && this.isMetric !== undefined) {
      return this.isMetric
        ? `${this.removeDecimalPointsPipe.transform(eff, 0)} kWh/100km`
        : `${this.removeDecimalPointsPipe.transform(this.kWhKmToMilesPipe.transform(eff), 1)} mi/kWh`;
    } else {
      return this.companyData?.companyAddress?.country === 'United States (US)'
        ? `${this.removeDecimalPointsPipe.transform(this.kWhKmToMilesPipe.transform(eff), 1)} mi/kWh`
        : `${this.removeDecimalPointsPipe.transform(eff, 0)} kWh/100km`;
    }
}
}
